import React, { useMemo } from "react";
import { useSelector } from "react-redux";

import { IgAnalyticsTooltip, IgTypography } from "@intelligems/ig-theme";
import { ExperienceFe } from "@intelligems/ig-types";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { getInitialStartFilterTime } from "@src/features/analytics/analyticsFilters/utils";
import { selectedDashboardFilters } from "@src/store/slices/dashboardFilters";
import {
  fullDateTimeConverterCommas,
  shortDateTimeConverter,
} from "@src/utils/dateConverters";

interface AnalyticsDateRangeProps {
  experience: ExperienceFe;
}
export const AnalyticsDateRange = ({ experience }: AnalyticsDateRangeProps) => {
  const dashboardFilters = useSelector(selectedDashboardFilters);
  const org = useGetOrg();
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");
  const endTime = useMemo(() => {
    if (dashboardFilters.endTime) {
      return dashboardFilters.endTime as number | null | undefined;
    }
    if (experience.status === "ended") {
      return experience.endedAtTs;
    }
    if (
      experience.category === "personalization" &&
      experience.status === "paused"
    ) {
      return experience.pausedAtTs;
    }
    return null;
  }, [dashboardFilters.endTime]);

  if (!dashboardFilters.startTime && !dashboardFilters.endTime) {
    return (
      <IgAnalyticsTooltip
        title={"Select a date range to filter your results to"}
      >
        <IgTypography>
          Range:{" "}
          <IgTypography igStyle={"semibold"}>
            All (
            {shortDateTimeConverter(
              getInitialStartFilterTime(experience, dashboardFilters),
              undefined,
              org?.data?.org?.timezone || undefined,
              false
            )}{" "}
            -{" "}
            {endTime === null
              ? "Now"
              : shortDateTimeConverter(
                  endTime,
                  undefined,
                  org?.data?.org?.timezone || undefined,
                  false
                )}
            )
          </IgTypography>
        </IgTypography>
      </IgAnalyticsTooltip>
    );
  }
  return (
    <IgAnalyticsTooltip
      title={
        <IgTypography>
          {fullDateTimeConverterCommas({
            date: dashboardFilters.startTime || experience.startedAtTs,
            timeZone: org?.data?.org?.timezone || undefined,
            locale: storeCurrencyConfig?.value?.locale,
            showTimeZoneName: true,
          })}{" "}
          -{" "}
          {endTime !== null
            ? fullDateTimeConverterCommas({
                date: endTime,
                timeZone: org?.data?.org?.timezone || undefined,
                locale: storeCurrencyConfig?.value?.locale,
                showTimeZoneName: true,
              })
            : "Now"}
        </IgTypography>
      }
    >
      <IgTypography igStyle={"semibold"}>
        <IgTypography>Range: </IgTypography>
        {shortDateTimeConverter(
          (dashboardFilters.startTime as number | null | undefined) ||
            experience.startedAtTs,
          undefined,
          org?.data?.org?.timezone || undefined,
          false
        )}{" "}
        -{" "}
        {endTime === null
          ? "Now"
          : shortDateTimeConverter(
              endTime,
              undefined,
              org?.data?.org?.timezone || undefined,
              false
            )}
      </IgTypography>
    </IgAnalyticsTooltip>
  );
};
